import { Product, ProductCasePack } from "@deliverr/commons-clients";
import { ProductCasePackData } from "@deliverr/commons-clients/lib/product/ProductCasePack";
import { CasePackDefaultData } from "common/clients/inbound/bulkUpload/CasePackDefaultData";
import { useCallback } from "react";
import { batch, useDispatch } from "react-redux";
import { productClient } from "Clients";
import { DEFAULT_WEIGHT_UNIT, DEFAULT_LENGTH_UNIT } from "common/constants/units.const";
import {
  setNewProductCasePack,
  setSelectedCasePackOption,
  setProductCasePackOptions,
  setMultipleProductCasePackOptions,
} from "../../../../selectProductCasePacks/SelectProductCasePackActions";
import { updateCaseQty } from "../../../SelectStorageProductsActions";
import { log } from "@deliverr/commons-logs";

export interface CasePackCreationRequest {
  product: Product;
  caseDimension: CasePackDefaultData;
}

export const useCasePackTemplate = () => {
  const dispatch = useDispatch();

  const createCasePackTemplate = useCallback(
    async (product: Product, caseDimension?: CasePackDefaultData): Promise<ProductCasePack | undefined> => {
      if (!caseDimension) {
        return undefined;
      }

      try {
        const { unitsPerCase, weight, weightUnit, height, length, width, dimensionUnit } = caseDimension;
        const unitStr = unitsPerCase === 1 ? "unit" : "units";
        const packName = `${unitsPerCase} ${unitStr} per box`;

        const casePackData: ProductCasePackData = {
          packOf: product.dsku,
          quantity: unitsPerCase,
          weight,
          weightUnit: weightUnit || DEFAULT_WEIGHT_UNIT,
          height,
          length,
          width,
          lengthUnit: dimensionUnit || DEFAULT_LENGTH_UNIT,
          packName,
        };

        const productCasePack = await productClient.createProductCasePack(casePackData);

        if (!productCasePack) {
          throw new Error("API returned empty case pack");
        }

        batch(() => {
          dispatch(setNewProductCasePack(product.dsku, productCasePack));
          dispatch(setSelectedCasePackOption(product.dsku, productCasePack));
          dispatch(updateCaseQty(product.dsku, productCasePack.quantity));
          // Add case pack to options so it appears in the UI dropdown
          if (productCasePack?.dsku) {
            dispatch(
              setProductCasePackOptions(product.dsku, {
                [productCasePack.dsku]: productCasePack,
              })
            );
          }
        });

        return productCasePack;
      } catch (error) {
        log.error(`Failed to create case pack for ${product.dsku}:`, error);
        return undefined;
      }
    },
    [dispatch]
  );

  /**
   * Optimized bulk case pack creation using createProductsCasePack API
   */
  const createBulkCasePackTemplates = useCallback(
    async (requests: CasePackCreationRequest[]): Promise<Record<string, ProductCasePack[]>> => {
      if (requests.length === 0) {
        return {};
      }

      try {
        // Prepare all case pack data for bulk creation
        const casePackDataArray: ProductCasePackData[] = requests.map(({ product, caseDimension }) => {
          const { unitsPerCase, weight, weightUnit, height, length, width, dimensionUnit } = caseDimension;
          const unitStr = unitsPerCase === 1 ? "unit" : "units";
          const packName = `${unitsPerCase} ${unitStr} per box`;

          return {
            packOf: product.dsku,
            quantity: unitsPerCase,
            weight,
            weightUnit: weightUnit || DEFAULT_WEIGHT_UNIT,
            height,
            length,
            width,
            lengthUnit: dimensionUnit || DEFAULT_LENGTH_UNIT,
            packName,
          };
        });

        // Use bulk API for better performance
        const response = await productClient.createProductsCasePack(casePackDataArray);

        if (!response.success || !response.data) {
          throw new Error(response.error ?? "Failed to create case packs");
        }

        // Process successful case pack creations
        const allCreatedCasePacks = response.data
          .filter((errorItem) => typeof errorItem.item === "object" && errorItem.item !== null)
          .map((errorItem) => errorItem.item as ProductCasePack);

        if (allCreatedCasePacks.length === 0) {
          throw new Error("No case packs were successfully created");
        }

        // Group case packs by product DSKU
        const casePacksByProduct: Record<string, ProductCasePack[]> = {};

        // Prepare bulk Redux updates for better performance
        const bulkUpdates = {
          newProductCasePacks: {} as Record<string, ProductCasePack>,
          caseQuantities: {} as Record<string, number>,
          casePackOptions: [] as Array<{ dsku: string; productCasePacks: Record<string, ProductCasePack> }>,
        };

        allCreatedCasePacks.forEach((casePack) => {
          if (!casePack?.packOf) {
            return;
          }

          const productDsku = casePack.packOf;

          if (!casePacksByProduct[productDsku]) {
            casePacksByProduct[productDsku] = [];
          }
          casePacksByProduct[productDsku].push(casePack);

          // Prepare bulk Redux data
          bulkUpdates.newProductCasePacks[productDsku] = casePack;
          bulkUpdates.caseQuantities[productDsku] = casePack.quantity;

          // Prepare case pack options for UI dropdown using bulk format
          if (casePack?.dsku) {
            const existingEntry = bulkUpdates.casePackOptions.find((entry) => entry.dsku === productDsku);
            if (existingEntry) {
              existingEntry.productCasePacks[casePack.dsku] = casePack;
            } else {
              bulkUpdates.casePackOptions.push({
                dsku: productDsku,
                productCasePacks: { [casePack.dsku]: casePack },
              });
            }
          }
        });

        // Dispatch all essential Redux updates using bulk operations for maximum performance
        try {
          batch(() => {
            // Dispatch all new case packs
            Object.entries(bulkUpdates.newProductCasePacks).forEach(([dsku, casePack]) => {
              dispatch(setNewProductCasePack(dsku, casePack));
            });

            // Dispatch all case quantities
            Object.entries(bulkUpdates.caseQuantities).forEach(([dsku, quantity]) => {
              dispatch(updateCaseQty(dsku, quantity));
            });

            // Use bulk action for case pack options - this is critical for performance and dropdown population
            if (bulkUpdates.casePackOptions.length > 0) {
              dispatch(setMultipleProductCasePackOptions(bulkUpdates.casePackOptions));
            }

            // NOTE: Selected case packs will be set by the caller (useCasePackOperations)
            // to avoid conflicts and ensure proper precedence of existing vs newly created case packs
          });
        } catch (reduxError) {
          log.error("Failed to dispatch Redux updates for case packs:", reduxError);
          // Don't show toast for Redux errors - they're not user-actionable
        }

        return casePacksByProduct;
      } catch (error) {
        log.error("Failed to create case pack templates in bulk:", error);
        return {};
      }
    },
    [dispatch]
  );

  return {
    createCasePackTemplate,
    createBulkCasePackTemplates,
  };
};
